import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import TicketCard from '../components/TicketCard';
import { Button } from '../components/ui/button';
import { Ticket, Plus, Download, Loader, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL as API } from '../config/api';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [checkingCrypto, setCheckingCrypto] = useState(false);

  // Check for crypto payment return
  const cryptoPayment = searchParams.get('crypto_payment');
  const chargeId = searchParams.get('charge_id');

  // Page-specific translations
  const pageTexts = {
    fr: {
      downloadPdf: "Télécharger PDF",
      testTicket: "Billet TEST",
      pdfSuccess: "PDF téléchargé avec succès !",
      pdfError: "Erreur lors du téléchargement du PDF",
      testSuccess: "Billet TEST créé ! Numéro:",
      testInfo: "Vérifiez votre email pour le billet avec QR code !",
      testError: "Erreur lors de la création du billet TEST",
      shareWhatsApp: "Partager",
      shareMessage: "🎟️ Je participe au Marathon de l'Avenir EDENLIFE 2026 et je peux gagner jusqu'à $450,000 USD! Rejoignez-moi et tentez votre chance: ",
      paymentTimeout: "Vérification du paiement expirée",
      paymentSuccess: "Paiement crypto réussi ! Vos billets sont prêts.",
      paymentFailed: "Paiement crypto échoué ou expiré",
      paymentVerifying: "Vérification de votre paiement crypto...",
      paymentCancelled: "Paiement crypto annulé"
    },
    en: {
      downloadPdf: "Download PDF",
      testTicket: "Test Ticket",
      pdfSuccess: "PDF downloaded successfully!",
      pdfError: "Error downloading PDF",
      testSuccess: "TEST ticket created! Number:",
      testInfo: "Check your email for the ticket with QR code!",
      testError: "Error creating TEST ticket",
      shareWhatsApp: "Share",
      shareMessage: "🎟️ I'm participating in the EDENLIFE Marathon 2026 and I could win up to $450,000 USD! Join me and try your luck: ",
      paymentTimeout: "Payment verification timed out",
      paymentSuccess: "Crypto payment successful! Your tickets are ready.",
      paymentFailed: "Crypto payment failed or expired",
      paymentVerifying: "Verifying your crypto payment...",
      paymentCancelled: "Crypto payment was cancelled"
    },
    ht: {
      downloadPdf: "Telechaje PDF",
      testTicket: "Tikè TEST",
      pdfSuccess: "PDF telechaje avèk siksè!",
      pdfError: "Erè nan telechaje PDF",
      testSuccess: "Tikè TEST kreye! Nimewo:",
      testInfo: "Tcheke imel ou pou tikè ak kòd QR!",
      testError: "Erè nan kreye tikè TEST",
      shareWhatsApp: "Pataje",
      shareMessage: "🎟️ Mwen patisipe nan Maraton EDENLIFE 2026 epi mwen ka genyen jiska $450,000 USD! Vin jwenn mwen epi eseye chans ou: ",
      paymentTimeout: "Verifikasyon peman an fini tan",
      paymentSuccess: "Peman kripto reyisi! Tikè ou yo pare.",
      paymentFailed: "Peman kripto echwe oswa ekspire",
      paymentVerifying: "Verifikasyon peman kripto ou...",
      paymentCancelled: "Peman kripto anile"
    },
    es: {
      downloadPdf: "Descargar PDF",
      testTicket: "Boleto TEST",
      pdfSuccess: "¡PDF descargado con éxito!",
      pdfError: "Error al descargar el PDF",
      testSuccess: "¡Boleto TEST creado! Número:",
      testInfo: "¡Revisa tu correo para el boleto con código QR!",
      testError: "Error al crear el boleto TEST",
      shareWhatsApp: "Compartir",
      shareMessage: "🎟️ ¡Participo en el Maratón EDENLIFE 2026 y podría ganar hasta $450,000 USD! Únete a mí y prueba tu suerte: ",
      paymentTimeout: "La verificación del pago expiró",
      paymentSuccess: "¡Pago en cripto exitoso! Tus boletos están listos.",
      paymentFailed: "El pago en cripto falló o expiró",
      paymentVerifying: "Verificando tu pago en cripto...",
      paymentCancelled: "Pago en cripto cancelado"
    },
    pt: {
      downloadPdf: "Baixar PDF",
      testTicket: "Bilhete TESTE",
      pdfSuccess: "PDF baixado com sucesso!",
      pdfError: "Erro ao baixar o PDF",
      testSuccess: "Bilhete TESTE criado! Número:",
      testInfo: "Verifique seu e-mail para o bilhete com código QR!",
      testError: "Erro ao criar o bilhete TESTE",
      shareWhatsApp: "Compartilhar",
      shareMessage: "🎟️ Estou participando da Maratona EDENLIFE 2026 e posso ganhar até $450,000 USD! Junte-se a mim e tente sua sorte: ",
      paymentTimeout: "A verificação do pagamento expirou",
      paymentSuccess: "Pagamento em cripto bem-sucedido! Seus bilhetes estão prontos.",
      paymentFailed: "Pagamento em cripto falhou ou expirou",
      paymentVerifying: "Verificando seu pagamento em cripto...",
      paymentCancelled: "Pagamento em cripto cancelado"
    }
  };

  const pt = pageTexts[i18n.language] || pageTexts.fr;

  const handleShareWhatsApp = () => {
    const shareUrl = `${window.location.origin}/purchase`;
    const message = encodeURIComponent(`${pt.shareMessage}${shareUrl}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  // Poll crypto payment status
  const pollCryptoPaymentStatus = async (chargeId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      toast.error(pt.paymentTimeout);
      setCheckingCrypto(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/crypto/status/${chargeId}`, {
        withCredentials: true,
      });

      if (response.data.payment_status === 'completed') {
        toast.success(pt.paymentSuccess);
        setCheckingCrypto(false);
        fetchTickets();
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.payment_status === 'expired' || response.data.payment_status === 'failed') {
        toast.error(pt.paymentFailed);
        setCheckingCrypto(false);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Still pending, poll again
      setTimeout(() => pollCryptoPaymentStatus(chargeId, attempts + 1), 3000);
    } catch (error) {
      console.error('Error checking crypto payment:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollCryptoPaymentStatus(chargeId, attempts + 1), 3000);
      } else {
        setCheckingCrypto(false);
      }
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets/my-tickets`, {
        withCredentials: true,
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/tickets/download-pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `billets_edenlife_${user.user_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(pt.pdfSuccess);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error(pt.pdfError);
    }
  };

  const handleBuyTestTicket = async () => {
    try {
      const response = await axios.post(`${API}/tickets/purchase-test`, {}, {
        withCredentials: true
      });
      toast.success(`${pt.testSuccess} ${response.data.ticket_number}`);
      toast.info(pt.testInfo);
      fetchTickets();
    } catch (error) {
      console.error('Failed to create test ticket:', error);
      toast.error(pt.testError);
    }
  };

  useEffect(() => {
    fetchTickets();
    
    // Check for crypto payment return
    if (cryptoPayment === 'success' && chargeId) {
      setCheckingCrypto(true);
      toast.info(pt.paymentVerifying);
      pollCryptoPaymentStatus(chargeId);
    } else if (cryptoPayment === 'cancelled') {
      toast.error(pt.paymentCancelled);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen pt-24 px-6 pb-12"
      style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}
      data-testid="dashboard-page"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1
                className="text-4xl md:text-5xl font-bold text-primary mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="dashboard-title"
              >
                {t('nav.dashboard')}
              </h1>
              <p className="text-xl text-gray-600">
                {t('nav.myTickets')}: <span className="font-bold text-primary">{tickets.length}</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {tickets.length > 0 && (
                <>
                  <Button
                    onClick={handleShareWhatsApp}
                    className="bg-[#25D366] text-white hover:bg-[#25D366]/90 rounded-full"
                    data-testid="share-whatsapp-button"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    {pt.shareWhatsApp}
                  </Button>
                  <Button
                    onClick={handleDownloadPDF}
                    variant="outline"
                    className="rounded-full"
                    data-testid="download-pdf-button"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    {pt.downloadPdf}
                  </Button>
                </>
              )}
              <Button
                onClick={handleBuyTestTicket}
                variant="outline"
                className="rounded-full border-accent text-accent hover:bg-accent hover:text-white"
                data-testid="buy-test-ticket-button"
              >
                🎫 {pt.testTicket}
              </Button>
              <Link to="/purchase">
                <Button
                  size="lg"
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full"
                  data-testid="buy-more-tickets-button"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {t('nav.buyTickets')}
                </Button>
              </Link>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-md rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <Ticket className="w-8 h-8 text-primary mr-3" />
              <h2 className="text-2xl font-bold text-primary">{t('tickets.myTickets')}</h2>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary mx-auto"></div>
                <p className="text-gray-600 mt-4">{t('common.loading')}</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12" data-testid="no-tickets-message">
                <Ticket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-xl text-gray-600 mb-6">{t('tickets.noTickets')}</p>
                <Link to="/purchase">
                  <Button className="bg-primary text-primary-foreground rounded-full">
                    {t('nav.buyTickets')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tickets.map((ticket, index) => (
                  <TicketCard key={ticket.ticket_id} ticket={ticket} index={index} />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;